import { api } from './apiClient';
import type { Page } from '@/types/api';
import type {
  AppNotification, Booking, GeoPointDto, Message, PlatformStats, PublicUser, Report, Review, Ride,
  Thread, User, Vehicle, Place, RidePreferences,
} from '@/types/models';

/** Every backend call the UI makes, typed, in one place. Components never build URLs. */
const P = '/api/v1';

export const usersApi = {
  me: () => api.get<User>(`${P}/users/me`),
  update: (body: { name: string; phone?: string; bio?: string }) => api.put<User>(`${P}/users/me`, body),
  get: (userId: string) => api.get<PublicUser>(`${P}/users/${userId}`),
  reviews: (userId: string, cursor?: string) =>
    api.get<Page<Review>>(`${P}/users/${userId}/reviews`, { query: { cursor } }),
  photoUploadUrl: (contentType: string) =>
    api.post<{ uploadUrl: string; objectKey: string }>(`${P}/users/me/photo-upload-url`, { contentType }),
  confirmPhoto: (objectKey: string) => api.post<User>(`${P}/users/me/photo`, { objectKey }),
};

export interface VehicleInput {
  make: string; model: string; year: number; color: string;
  vehicleType: string; seats: number; registrationNumber: string; isDefault: boolean;
}

export interface PlaceSuggestion { placeId: string; description: string }

export const locationsApi = {
  /** Free-text place search. A suggestion's id is its own "lat,lng", so choosing one
   * needs no second lookup. */
  autocomplete: (q: string) =>
    api.get<PlaceSuggestion[]>(`${P}/locations/autocomplete`, { query: { q } }),
  /** Names the place at a dropped pin. The coordinates are the caller's own and come
   * back unchanged; only the label is the provider's. */
  reverse: (lat: number, lng: number) => api.get<Place>(`${P}/locations/reverse`, { query: { lat, lng } }),
};

export const vehiclesApi = {
  list: () => api.get<Vehicle[]>(`${P}/vehicles`),
  create: (body: VehicleInput) => api.post<Vehicle>(`${P}/vehicles`, body),
  update: (id: string, body: { color: string; isDefault: boolean }) => api.put<Vehicle>(`${P}/vehicles/${id}`, body),
  remove: (id: string) => api.delete<void>(`${P}/vehicles/${id}`),
};

export interface UpdateRideInput {
  notes?: string;
  preferences?: RidePreferences;
  departureLocalDate?: string;
  departureLocalTime?: string;
  totalSeats?: number;
  pricePerSeatMinor?: number;
  instantBooking?: boolean;
}

export interface CreateRideInput {
  origin: Place; destination: Place;
  departureLocalDate: string; departureLocalTime: string; departureZone: string;
  totalSeats: number; pricePerSeatMinor: number; currency: string; vehicleId: string;
  notes?: string; preferences: RidePreferences;
  /** True accepts seat requests immediately; false keeps the driver-review step. */
  instantBooking: boolean;
  /** The route the driver chose from `routeOptions`. The server re-validates it against
   * the ride's own endpoints, so this is a choice, not an assertion. */
  routePolyline?: GeoPointDto[];
}

export interface SearchParams {
  originLat: number; originLng: number; destLat: number; destLng: number;
  date: string; passengers: number; radiusKm?: number;
}

export interface RouteSearchParams {
  pickupLat: number; pickupLng: number; dropLat: number; dropLng: number;
  date: string; passengers: number; toleranceKm?: number;
}

export interface RoutePreview {
  polyline: GeoPointDto[];
  distanceKm: number;
  durationMinutes: number;
}

export const ridesApi = {
  search: (q: SearchParams) => api.get<Page<Ride>>(`${P}/rides/search`, { query: { ...q } }),
  // Matches by proximity to the driver's actual route, not just their exact origin and
  // destination -- see docs on SearchService#searchAlongRoute. Used for the public
  // search page; the plain `search` above stays available for anything that wants exact
  // origin/destination proximity instead.
  searchAlongRoute: (q: RouteSearchParams) => api.get<Page<Ride>>(`${P}/rides/search-route`, { query: { ...q } }),
  routePreview: (body: { origin: Place; destination: Place }) =>
    api.post<RoutePreview>(`${P}/rides/route-preview`, body),
  routeOptions: (body: { origin: Place; destination: Place }) =>
    api.post<{ routes: RoutePreview[] }>(`${P}/rides/route-options`, body),
  get: (id: string) => api.get<Ride>(`${P}/rides/${id}`),
  create: (body: CreateRideInput) => api.post<Ride>(`${P}/rides`, body),
  update: (id: string, body: UpdateRideInput) => api.put<Ride>(`${P}/rides/${id}`, body),
  cancel: (id: string) => api.delete<Ride>(`${P}/rides/${id}`),
  complete: (id: string) => api.post<Ride>(`${P}/rides/${id}/complete`),
  mine: (cursor?: string) => api.get<Page<Ride>>(`${P}/rides/mine`, { query: { cursor } }),
};

export const bookingsApi = {
  request: (rideId: string, body: { seats: number; message?: string; pickup?: Place; drop?: Place },
            idempotencyKey: string) =>
    api.post<Booking>(`${P}/rides/${rideId}/bookings`, body, { idempotencyKey }),
  /** What one seat costs for part of this ride's route. Quoted by the server from the
   * ride's own geometry, so the price shown is the price charged. */
  quote: (rideId: string, q: { pickupLat: number; pickupLng: number; dropLat: number; dropLng: number }) =>
    api.get<{ pricePerSeatMinor: number; segmentDistanceKm: number }>(`${P}/rides/${rideId}/quote`, { query: { ...q } }),
  forRide: (rideId: string) => api.get<Booking[]>(`${P}/rides/${rideId}/bookings`),
  mine: (cursor?: string) => api.get<Page<Booking>>(`${P}/bookings`, { query: { cursor } }),
  get: (id: string) => api.get<Booking>(`${P}/bookings/${id}`),
  setStatus: (id: string, status: string, reason?: string) =>
    api.put<Booking>(`${P}/bookings/${id}/status`, { status, reason }),
};

export const reviewsApi = {
  submit: (rideId: string, body: { revieweeId: string; rating: number; comment?: string }) =>
    api.post<Review>(`${P}/rides/${rideId}/reviews`, body),
};

export const notificationsApi = {
  list: (cursor?: string) => api.get<Page<AppNotification>>(`${P}/notifications`, { query: { cursor } }),
  markRead: (sortKey: string) => api.post<void>(`${P}/notifications/read`, undefined, { query: { sortKey } }),
};

export const messagesApi = {
  threads: () => api.get<Thread[]>(`${P}/threads`),
  conversation: (rideId: string, counterpartId: string) =>
    api.get<Message[]>(`${P}/messages`, { query: { rideId, counterpartId } }),
  send: (body: { rideId: string; receiverId: string; body: string }) => api.post<Message>(`${P}/messages`, body),
};

export const reportsApi = {
  create: (body: { targetType: string; targetId: string; reason: string; description?: string }) =>
    api.post<Report>(`${P}/reports`, body),
};

export const adminApi = {
  stats: (days = 14) => api.get<PlatformStats>(`${P}/admin/stats`, { query: { days } }),
  users: (q?: string) => api.get<User[]>(`${P}/admin/users`, { query: { q } }),
  suspend: (id: string) => api.post<User>(`${P}/admin/users/${id}/suspend`),
  reinstate: (id: string) => api.post<User>(`${P}/admin/users/${id}/reinstate`),
  rides: () => api.get<Ride[]>(`${P}/admin/rides`),
  bookings: () => api.get<Booking[]>(`${P}/admin/bookings`),
  reports: (status = 'OPEN') => api.get<Report[]>(`${P}/admin/reports`, { query: { status } }),
  resolveReport: (id: string, status: 'RESOLVED' | 'DISMISSED', resolution: string) =>
    api.post<Report>(`${P}/admin/reports/${id}/resolve`, { status, resolution }),
};
